import React, { useState, useEffect } from 'react';
import './StrategyForm.css';

// Strategy Templates
const STRATEGY_TEMPLATES = {
  safe: {
    name: 'Safe Conservative',
    config: {
      timeframe: '1d',
      strategyType: 'indicator',
      marketRegime: { enabled: true, trendFilter: 'movingAverages', short: 50, long: 200 },
      entrySetup: {
        rsi: { enabled: true, oversold: 20, overbought: 80, buyZoneMax: 30 },
        relativeVolume: { enabled: false, threshold: 10, period: 20 },
        priceAction: { enabled: false, lookback: 5 },
      },
      entryTrigger: { macd: { enabled: true } },
      exitConditions: { macdCrossunder: true, rsiOverbought: true },
      tradeControls: { cooldownBars: 10, maxTradesPerDay: 2, maxOpenPositions: 1 },
      riskManagement: { riskPerTrade: 0, rewardRiskRatio: 0, stopLoss: 3, takeProfit: 6, maxPosition: 5000 },
      qualityFilter: { enabled: false, market: { enabled: false, ticker: 'SPY', allowedState: 'bullish_or_neutral' }, liquidity: { enabled: false, minAvgVolume: 2000000, minCurrentVolumePct: 40, minDollarVolume: 20000000 }, company: { enabled: false, requirePositiveEPS: true, minMarketCap: 3000000000, unprofitableOverrideCap: 0 }, minScore: 65, showReasonBreakdown: true, showWatchlistCandidates: true },
    }
  },
  moderate: {
    name: 'Moderate Balanced',
    config: {
      timeframe: '1d',
      strategyType: 'indicator',
      marketRegime: { enabled: true, trendFilter: 'movingAverages', short: 20, long: 50 },
      entrySetup: {
        rsi: { enabled: true, oversold: 30, overbought: 70, buyZoneMax: 40 },
        relativeVolume: { enabled: false, threshold: 10, period: 20 },
        priceAction: { enabled: false, lookback: 5 },
      },
      entryTrigger: { macd: { enabled: true } },
      exitConditions: { macdCrossunder: true, rsiOverbought: true },
      tradeControls: { cooldownBars: 5, maxTradesPerDay: 3, maxOpenPositions: 2 },
      riskManagement: { riskPerTrade: 0, rewardRiskRatio: 0, stopLoss: 2, takeProfit: 5, maxPosition: 10000 },
      qualityFilter: { enabled: true, market: { enabled: true, ticker: 'SPY', allowedState: 'bullish_or_neutral' }, liquidity: { enabled: true, minAvgVolume: 2000000, minCurrentVolumePct: 40, minDollarVolume: 20000000 }, company: { enabled: true, requirePositiveEPS: true, minMarketCap: 3000000000, unprofitableOverrideCap: 0 }, minScore: 65, showReasonBreakdown: true, showWatchlistCandidates: true },
    }
  },
  risky: {
    name: 'Aggressive Growth',
    config: {
      timeframe: '1d',
      strategyType: 'indicator',
      marketRegime: { enabled: false, trendFilter: 'movingAverages', short: 10, long: 20 },
      entrySetup: {
        rsi: { enabled: true, oversold: 40, overbought: 60, buyZoneMax: 50 },
        relativeVolume: { enabled: false, threshold: 10, period: 20 },
        priceAction: { enabled: false, lookback: 5 },
      },
      entryTrigger: { macd: { enabled: true } },
      exitConditions: { macdCrossunder: true, rsiOverbought: true },
      tradeControls: { cooldownBars: 2, maxTradesPerDay: 5, maxOpenPositions: 3 },
      riskManagement: { riskPerTrade: 0, rewardRiskRatio: 0, stopLoss: 1.5, takeProfit: 4, maxPosition: 20000 },
      qualityFilter: { enabled: false, market: { enabled: false, ticker: 'SPY', allowedState: 'any' }, liquidity: { enabled: false, minAvgVolume: 1000000, minCurrentVolumePct: 30, minDollarVolume: 10000000 }, company: { enabled: false, requirePositiveEPS: false, minMarketCap: 1000000000, unprofitableOverrideCap: 0 }, minScore: 50, showReasonBreakdown: true, showWatchlistCandidates: true },
    }
  },
  momentum: {
    name: 'Momentum Runner',
    config: {
      timeframe: '5m',
      strategyType: 'momentum',
      marketRegime: { enabled: false, trendFilter: 'movingAverages', short: 10, long: 20 },
      entrySetup: {
        rsi: { enabled: false, oversold: 30, overbought: 70, buyZoneMax: 40 },
        relativeVolume: { enabled: true, threshold: 10, period: 20 },
        priceAction: { enabled: true, lookback: 5 },
      },
      entryTrigger: { macd: { enabled: false } },
      exitConditions: { macdCrossunder: false, rsiOverbought: false },
      tradeControls: { cooldownBars: 0, maxTradesPerDay: 3, maxOpenPositions: 2 },
      riskManagement: { riskPerTrade: 750, rewardRiskRatio: 1.25, stopLoss: 2, takeProfit: 2.5, maxPosition: 15000 },
      qualityFilter: { enabled: false, market: { enabled: false, ticker: 'SPY', allowedState: 'any' }, liquidity: { enabled: false, minAvgVolume: 1000000, minCurrentVolumePct: 30, minDollarVolume: 10000000 }, company: { enabled: false, requirePositiveEPS: false, minMarketCap: 1000000000, unprofitableOverrideCap: 0 }, minScore: 50, showReasonBreakdown: true, showWatchlistCandidates: true },
    }
  },
  custom: {
    name: 'Custom Strategy',
    config: {
      timeframe: '1d',
      strategyType: 'indicator',
      marketRegime: { enabled: true, trendFilter: 'movingAverages', short: 20, long: 50 },
      entrySetup: {
        rsi: { enabled: true, oversold: 30, overbought: 70, buyZoneMax: 40 },
        relativeVolume: { enabled: false, threshold: 10, period: 20 },
        priceAction: { enabled: false, lookback: 5 },
      },
      entryTrigger: { macd: { enabled: true } },
      exitConditions: { macdCrossunder: true, rsiOverbought: true },
      tradeControls: { cooldownBars: 5, maxTradesPerDay: 3, maxOpenPositions: 2 },
      riskManagement: { riskPerTrade: 0, rewardRiskRatio: 0, stopLoss: 2, takeProfit: 5, maxPosition: 10000 },
      qualityFilter: { enabled: false, market: { enabled: false, ticker: 'SPY', allowedState: 'bullish_or_neutral' }, liquidity: { enabled: false, minAvgVolume: 2000000, minCurrentVolumePct: 40, minDollarVolume: 20000000 }, company: { enabled: false, requirePositiveEPS: true, minMarketCap: 3000000000, unprofitableOverrideCap: 0 }, minScore: 65, showReasonBreakdown: true, showWatchlistCandidates: true },
    }
  }
};

function StrategyForm({ initialData, onSubmit, onCancel, loading = false }) {
  const [selectedTemplate, setSelectedTemplate] = useState(initialData ? 'custom' : '');
  const [formData, setFormData] = useState(
    initialData || {
      strategy_name: '',
      config: STRATEGY_TEMPLATES.custom.config,
    }
  );

  const [errors, setErrors] = useState({});

  // Auto-calculate take profit from stop loss × R:R ratio for momentum strategies
  useEffect(() => {
    if (formData.config.strategyType !== 'momentum') return;
    const { stopLoss, rewardRiskRatio } = formData.config.riskManagement;
    if (rewardRiskRatio > 0 && stopLoss > 0) {
      const calculatedTP = Number((stopLoss * rewardRiskRatio).toFixed(1));
      if (formData.config.riskManagement.takeProfit !== calculatedTP) {
        setFormData((prev) => ({
          ...prev,
          config: {
            ...prev.config,
            riskManagement: {
              ...prev.config.riskManagement,
              takeProfit: calculatedTP,
            },
          },
        }));
      }
    }
  }, [formData.config.strategyType, formData.config.riskManagement.stopLoss, formData.config.riskManagement.rewardRiskRatio]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    if (template && STRATEGY_TEMPLATES[template]) {
      setFormData({
        strategy_name: STRATEGY_TEMPLATES[template].name,
        config: { ...STRATEGY_TEMPLATES[template].config },
      });
    }
  };

  const handleNameChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      strategy_name: e.target.value,
    }));
    if (errors.strategy_name) {
      setErrors((prev) => ({ ...prev, strategy_name: '' }));
    }
  };

  const handleTimeframeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        timeframe: e.target.value,
      },
    }));
  };

  const handleRegimeToggle = () => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        marketRegime: {
          ...prev.config.marketRegime,
          enabled: !prev.config.marketRegime.enabled,
        },
      },
    }));
  };

  const handleRegimeChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        marketRegime: {
          ...prev.config.marketRegime,
          [field]: isNaN(value) ? value : Number(value),
        },
      },
    }));
  };

  const handleSetupToggle = (indicator) => {
    setFormData((prev) => {
      const defaults = {
        rsi: { enabled: false, oversold: 30, overbought: 70, buyZoneMax: 40 },
        relativeVolume: { enabled: false, threshold: 10, period: 20 },
        priceAction: { enabled: false, lookback: 5 },
      };
      const current = prev.config.entrySetup[indicator] || defaults[indicator] || { enabled: false };
      return {
        ...prev,
        config: {
          ...prev.config,
          entrySetup: {
            ...prev.config.entrySetup,
            [indicator]: {
              ...current,
              enabled: !current.enabled,
            },
          },
        },
      };
    });
  };

  const handleSetupChange = (indicator, field, value) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        entrySetup: {
          ...prev.config.entrySetup,
          [indicator]: {
            ...prev.config.entrySetup[indicator],
            [field]: isNaN(value) ? value : Number(value),
          },
        },
      },
    }));
  };

  const handleTriggerToggle = (indicator) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        entryTrigger: {
          ...prev.config.entryTrigger,
          [indicator]: {
            ...prev.config.entryTrigger[indicator],
            enabled: !prev.config.entryTrigger[indicator].enabled,
          },
        },
      },
    }));
  };

  const handleExitConditionToggle = (condition) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        exitConditions: {
          ...prev.config.exitConditions,
          [condition]: !prev.config.exitConditions[condition],
        },
      },
    }));
  };

  const handleTradeControlChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        tradeControls: {
          ...prev.config.tradeControls,
          [field]: isNaN(value) ? value : Number(value),
        },
      },
    }));
  };

  const handleRiskChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        riskManagement: {
          ...prev.config.riskManagement,
          [field]: isNaN(value) ? value : Number(value),
        },
      },
    }));
  };

  const QF_DEFAULTS = {
    enabled: false,
    market: { enabled: false, ticker: 'SPY', allowedState: 'bullish_or_neutral' },
    liquidity: { enabled: false, minAvgVolume: 2000000, minCurrentVolumePct: 40, minDollarVolume: 20000000 },
    company: { enabled: false, requirePositiveEPS: true, minMarketCap: 3000000000, unprofitableOverrideCap: 0 },
    minScore: 65,
    showReasonBreakdown: true,
    showWatchlistCandidates: true,
  };

  const handleQualityFilterToggle = (path) => {
    setFormData((prev) => {
      const qf = { ...QF_DEFAULTS, ...prev.config.qualityFilter };
      qf.market = { ...QF_DEFAULTS.market, ...qf.market };
      qf.liquidity = { ...QF_DEFAULTS.liquidity, ...qf.liquidity };
      qf.company = { ...QF_DEFAULTS.company, ...qf.company };
      if (path === 'enabled') {
        qf.enabled = !qf.enabled;
      } else if (path === 'market.enabled') {
        qf.market = { ...qf.market, enabled: !qf.market.enabled };
      } else if (path === 'liquidity.enabled') {
        qf.liquidity = { ...qf.liquidity, enabled: !qf.liquidity.enabled };
      } else if (path === 'company.enabled') {
        qf.company = { ...qf.company, enabled: !qf.company.enabled };
      } else if (path === 'showReasonBreakdown') {
        qf.showReasonBreakdown = !qf.showReasonBreakdown;
      } else if (path === 'showWatchlistCandidates') {
        qf.showWatchlistCandidates = !qf.showWatchlistCandidates;
      } else if (path === 'company.requirePositiveEPS') {
        qf.company = { ...qf.company, requirePositiveEPS: !qf.company.requirePositiveEPS };
      }
      return { ...prev, config: { ...prev.config, qualityFilter: qf } };
    });
  };

  const handleQualityFilterChange = (section, field, value) => {
    setFormData((prev) => {
      const qf = { ...QF_DEFAULTS, ...prev.config.qualityFilter };
      qf.market = { ...QF_DEFAULTS.market, ...qf.market };
      qf.liquidity = { ...QF_DEFAULTS.liquidity, ...qf.liquidity };
      qf.company = { ...QF_DEFAULTS.company, ...qf.company };
      if (section === 'root') {
        qf[field] = isNaN(value) ? value : Number(value);
      } else {
        qf[section] = { ...qf[section], [field]: isNaN(value) ? value : Number(value) };
      }
      return { ...prev, config: { ...prev.config, qualityFilter: qf } };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.strategy_name.trim()) {
      newErrors.strategy_name = 'Strategy name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Transform form data to match backend expected structure
      const backendConfig = {
        ...formData,
        config: {
          timeframe: formData.config.timeframe,
          strategyType: formData.config.strategyType || 'indicator',
          marketRegime: formData.config.marketRegime,
          // Convert entrySetup/entryTrigger to indicators structure
          indicators: {
            rsi: formData.config.entrySetup.rsi.enabled ? {
              enabled: true,
              period: 14,
              oversold: formData.config.entrySetup.rsi.oversold,
              overbought: formData.config.entrySetup.rsi.overbought
            } : { enabled: false },
            macd: formData.config.entryTrigger.macd.enabled ? {
              enabled: true,
              fastPeriod: 12,
              slowPeriod: 26,
              signalPeriod: 9
            } : { enabled: false },
            movingAverages: formData.config.marketRegime.enabled ? {
              enabled: true,
              short: formData.config.marketRegime.short,
              long: formData.config.marketRegime.long
            } : { enabled: false },
            relativeVolume: formData.config.entrySetup.relativeVolume?.enabled ? {
              enabled: true,
              threshold: formData.config.entrySetup.relativeVolume.threshold || 10,
              period: formData.config.entrySetup.relativeVolume.period || 20
            } : { enabled: false },
            priceAction: formData.config.entrySetup.priceAction?.enabled ? {
              enabled: true,
              lookback: formData.config.entrySetup.priceAction.lookback || 5
            } : { enabled: false },
          },
          // Keep original structure for other features (not used by signal generation yet)
          entrySetup: formData.config.entrySetup,
          entryTrigger: formData.config.entryTrigger,
          exitConditions: formData.config.exitConditions,
          tradeControls: formData.config.tradeControls,
          riskManagement: formData.config.riskManagement,
          qualityFilter: formData.config.qualityFilter,
        }
      };
      onSubmit(backendConfig);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="strategy-form">
      {/* Strategy Template Selector - Only show when creating new */}
      {!initialData && (
        <div className="form-section template-selector">
          <h3>Choose a Strategy Template</h3>
          <p className="section-description">
            Start with a pre-built template or build your own from scratch
          </p>
          <div className="template-grid">
            <button
              type="button"
              className={`template-card ${selectedTemplate === 'safe' ? 'selected' : ''}`}
              onClick={() => handleTemplateSelect('safe')}
            >
              <div className="template-icon">🛡️</div>
              <h4>Safe</h4>
              <p>Conservative approach with strict filters and lower risk</p>
              <ul className="template-features">
                <li>Long-term trend following</li>
                <li>Strict entry conditions</li>
                <li>Lower position sizes</li>
              </ul>
            </button>
            
            <button
              type="button"
              className={`template-card ${selectedTemplate === 'moderate' ? 'selected' : ''}`}
              onClick={() => handleTemplateSelect('moderate')}
            >
              <div className="template-icon">⚖️</div>
              <h4>Moderate</h4>
              <p>Balanced risk/reward with standard parameters</p>
              <ul className="template-features">
                <li>Medium-term trends</li>
                <li>Balanced entry rules</li>
                <li>Moderate position sizing</li>
              </ul>
            </button>
            
            <button
              type="button"
              className={`template-card ${selectedTemplate === 'risky' ? 'selected' : ''}`}
              onClick={() => handleTemplateSelect('risky')}
            >
              <div className="template-icon">🚀</div>
              <h4>Aggressive</h4>
              <p>Active trading with relaxed filters for more opportunities</p>
              <ul className="template-features">
                <li>Short-term momentum</li>
                <li>Relaxed entry filters</li>
                <li>Larger position sizes</li>
              </ul>
            </button>
            
            <button
              type="button"
              className={`template-card ${selectedTemplate === 'momentum' ? 'selected' : ''}`}
              onClick={() => handleTemplateSelect('momentum')}
            >
              <div className="template-icon">⚡</div>
              <h4>Momentum</h4>
              <p>Trade high-volume momentum runners at market open</p>
              <ul className="template-features">
                <li>Relative volume filtering</li>
                <li>Intraday momentum</li>
                <li>Fixed dollar risk ($750)</li>
              </ul>
            </button>
            
            <button
              type="button"
              className={`template-card ${selectedTemplate === 'custom' ? 'selected' : ''}`}
              onClick={() => handleTemplateSelect('custom')}
            >
              <div className="template-icon">⚙️</div>
              <h4>Custom</h4>
              <p>Start from scratch and customize everything</p>
              <ul className="template-features">
                <li>Full control</li>
                <li>Set your own rules</li>
                <li>Advanced users</li>
              </ul>
            </button>
          </div>
        </div>
      )}

      {/* Strategy Name */}
      {selectedTemplate && (
      <>
      <div className="form-section">
        <h3>Basic Information</h3>
        <div className="form-group">
          <label htmlFor="strategy_name">Strategy Name *</label>
          <input
            type="text"
            id="strategy_name"
            value={formData.strategy_name}
            onChange={handleNameChange}
            placeholder="e.g. MACD Momentum Strategy"
            className={errors.strategy_name ? 'error' : ''}
          />
          {errors.strategy_name && (
            <span className="error-message">{errors.strategy_name}</span>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="timeframe">Timeframe *</label>
          <select
            id="timeframe"
            value={formData.config.timeframe}
            onChange={handleTimeframeChange}
          >
            <option value="1m">1 Minute</option>
            <option value="5m">5 Minutes</option>
            <option value="15m">15 Minutes</option>
            <option value="1h">1 Hour</option>
            <option value="4h">4 Hours</option>
            <option value="1d">Daily</option>
            <option value="1wk">Weekly</option>
          </select>
          <p className="info-text">All indicators will use this timeframe</p>
        </div>
      </div>

      {/* Market Regime */}
      <div className="form-section">
        <h3>Market Regime (Trend Filter)</h3>
        <p className="section-desc">
          Defines when trading is allowed. Only trades in favorable market conditions.
        </p>
        <div className="indicator-card">
          <div className="indicator-header">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.config.marketRegime.enabled}
                onChange={handleRegimeToggle}
              />
              <span>Enable Trend Filter (Moving Averages)</span>
            </label>
          </div>
          {formData.config.marketRegime.enabled && (
            <div className="indicator-settings">
              <p className="info-text">
                Trades only when short MA &gt; long MA (bullish trend)
              </p>
              <div className="setting-row">
                <div className="setting-group">
                  <label>Short MA Period</label>
                  <input
                    type="number"
                    min="5"
                    max="50"
                    value={formData.config.marketRegime.short}
                    onChange={(e) =>
                      handleRegimeChange('short', e.target.value)
                    }
                  />
                </div>
                <div className="setting-group">
                  <label>Long MA Period</label>
                  <input
                    type="number"
                    min="20"
                    max="200"
                    value={formData.config.marketRegime.long}
                    onChange={(e) =>
                      handleRegimeChange('long', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Entry Setup */}
      <div className="form-section">
        <h3>Entry Setup (Conditions)</h3>
        <p className="section-desc">
          Defines favorable conditions for entry, but does NOT trigger trades.
        </p>
        <div className="indicator-card">
          <div className="indicator-header">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.config.entrySetup.rsi.enabled}
                onChange={() => handleSetupToggle('rsi')}
              />
              <span>RSI Setup Filter</span>
            </label>
          </div>
          {formData.config.entrySetup.rsi.enabled && (
            <div className="indicator-settings">
              <p className="info-text">
                RSI defines the buy zone range, not the trigger
              </p>
              <div className="setting-row">
                <div className="setting-group">
                  <label>Oversold Level</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={formData.config.entrySetup.rsi.oversold}
                    onChange={(e) =>
                      handleSetupChange('rsi', 'oversold', e.target.value)
                    }
                  />
                </div>
                <div className="setting-group">
                  <label>Buy Zone Max</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.config.entrySetup.rsi.buyZoneMax}
                    onChange={(e) =>
                      handleSetupChange('rsi', 'buyZoneMax', e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="setting-group">
                <label>Overbought Level (for exits)</label>
                <input
                  type="number"
                  min="50"
                  max="100"
                  value={formData.config.entrySetup.rsi.overbought}
                  onChange={(e) =>
                    handleSetupChange('rsi', 'overbought', e.target.value)
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* Relative Volume Filter */}
        <div className="indicator-card">
          <div className="indicator-header">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.config.entrySetup.relativeVolume?.enabled || false}
                onChange={() => handleSetupToggle('relativeVolume')}
              />
              <span>Relative Volume Filter</span>
            </label>
          </div>
          {formData.config.entrySetup.relativeVolume?.enabled && (
            <div className="indicator-settings">
              <p className="info-text">
                Only trade stocks with unusual volume activity. A value of 10 means 10x average volume.
              </p>
              <div className="setting-row">
                <div className="setting-group">
                  <label>Volume Threshold (x)</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    step="0.5"
                    value={formData.config.entrySetup.relativeVolume.threshold}
                    onChange={(e) =>
                      handleSetupChange('relativeVolume', 'threshold', e.target.value)
                    }
                  />
                </div>
                <div className="setting-group">
                  <label>Average Period (days)</label>
                  <input
                    type="number"
                    min="5"
                    max="50"
                    value={formData.config.entrySetup.relativeVolume.period}
                    onChange={(e) =>
                      handleSetupChange('relativeVolume', 'period', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Price Action Filter */}
        <div className="indicator-card">
          <div className="indicator-header">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.config.entrySetup.priceAction?.enabled || false}
                onChange={() => handleSetupToggle('priceAction')}
              />
              <span>Price Action Momentum</span>
            </label>
          </div>
          {formData.config.entrySetup.priceAction?.enabled && (
            <div className="indicator-settings">
              <p className="info-text">
                Confirms price is trending upward. Only enters when momentum is in your favor.
              </p>
              <div className="setting-row">
                <div className="setting-group">
                  <label>Lookback Period (bars)</label>
                  <input
                    type="number"
                    min="2"
                    max="20"
                    value={formData.config.entrySetup.priceAction.lookback}
                    onChange={(e) =>
                      handleSetupChange('priceAction', 'lookback', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Entry Trigger */}
      <div className="form-section">
        <h3>Entry Trigger (Timing)</h3>
        <p className="section-desc">
          The actual signal that executes the trade when setup is ready.
        </p>
        <div className="indicator-card">
          <div className="indicator-header">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.config.entryTrigger.macd.enabled}
                onChange={() => handleTriggerToggle('macd')}
              />
              <span>MACD Crossover Trigger</span>
            </label>
          </div>
          {formData.config.entryTrigger.macd.enabled && (
            <div className="indicator-settings">
              <p className="info-text">
                Buys when MACD crosses above signal line (bullish crossover)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Exit Conditions */}
      <div className="form-section">
        <h3>Exit Conditions</h3>
        <p className="section-desc">
          Conditions that trigger selling or closing positions.
        </p>
        <div className="conditions-list">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.config.exitConditions.macdCrossunder}
              onChange={() => handleExitConditionToggle('macdCrossunder')}
            />
            <span>MACD Bearish Crossover</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.config.exitConditions.rsiOverbought}
              onChange={() => handleExitConditionToggle('rsiOverbought')}
            />
            <span>RSI Overbought</span>
          </label>
        </div>
      </div>

      {/* Trade Controls */}
      <div className="form-section">
        <h3>Trade Controls</h3>
        <p className="section-desc">
          Prevent overtrading and manage trade frequency.
        </p>
        <div className="risk-grid">
          <div className="form-group">
            <label htmlFor="cooldownBars">Cooldown (bars)</label>
            <input
              type="number"
              id="cooldownBars"
              min="0"
              max="50"
              value={formData.config.tradeControls.cooldownBars}
              onChange={(e) =>
                handleTradeControlChange('cooldownBars', e.target.value)
              }
            />
            <p className="info-text">Minimum bars between trades on same symbol</p>
          </div>
          <div className="form-group">
            <label htmlFor="maxTradesPerDay">Max Trades/Day</label>
            <input
              type="number"
              id="maxTradesPerDay"
              min="1"
              max="20"
              value={formData.config.tradeControls.maxTradesPerDay}
              onChange={(e) =>
                handleTradeControlChange('maxTradesPerDay', e.target.value)
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="maxOpenPositions">Max Open Positions</label>
            <input
              type="number"
              id="maxOpenPositions"
              min="1"
              max="10"
              value={formData.config.tradeControls.maxOpenPositions}
              onChange={(e) =>
                handleTradeControlChange('maxOpenPositions', e.target.value)
              }
            />
          </div>
        </div>
      </div>

      {/* Trade Quality Filter */}
      <div className="form-section">
        <h3>Trade Quality Filter</h3>
        <p className="section-desc">
          Optional layer that scores and filters signals beyond technical validity. Rejects low-quality setups or downgrades them to watchlist candidates.
        </p>
        <div className="indicator-card">
          <div className="indicator-header">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.config.qualityFilter?.enabled || false}
                onChange={() => handleQualityFilterToggle('enabled')}
              />
              <span>Enable Trade Quality Filter</span>
            </label>
          </div>
          {formData.config.qualityFilter?.enabled && (
            <div className="indicator-settings">
              {/* Market Quality */}
              <div className="indicator-card" style={{ marginTop: '0.5rem' }}>
                <div className="indicator-header">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.config.qualityFilter.market?.enabled || false}
                      onChange={() => handleQualityFilterToggle('market.enabled')}
                    />
                    <span>Market Quality Filter</span>
                  </label>
                </div>
                {formData.config.qualityFilter.market?.enabled && (
                  <div className="indicator-settings">
                    <p className="info-text">Block buy signals when the broader market is unfavorable (uses SPY trend).</p>
                    <div className="setting-row">
                      <div className="setting-group">
                        <label>Market Proxy Ticker</label>
                        <input
                          type="text"
                          value={formData.config.qualityFilter.market.ticker || 'SPY'}
                          onChange={(e) => handleQualityFilterChange('market', 'ticker', e.target.value.toUpperCase())}
                        />
                      </div>
                      <div className="setting-group">
                        <label>Allowed Market State</label>
                        <select
                          value={formData.config.qualityFilter.market.allowedState || 'bullish_or_neutral'}
                          onChange={(e) => handleQualityFilterChange('market', 'allowedState', e.target.value)}
                        >
                          <option value="bullish_only">Bullish Only</option>
                          <option value="bullish_or_neutral">Bullish or Neutral</option>
                          <option value="any">Any</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Liquidity Filter */}
              <div className="indicator-card" style={{ marginTop: '0.5rem' }}>
                <div className="indicator-header">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.config.qualityFilter.liquidity?.enabled || false}
                      onChange={() => handleQualityFilterToggle('liquidity.enabled')}
                    />
                    <span>Minimum Liquidity / Volume Filter</span>
                  </label>
                </div>
                {formData.config.qualityFilter.liquidity?.enabled && (
                  <div className="indicator-settings">
                    <p className="info-text">Reject low-volume, illiquid stocks.</p>
                    <div className="setting-row">
                      <div className="setting-group">
                        <label>Min Average Daily Volume</label>
                        <input
                          type="number"
                          min="0"
                          step="100000"
                          value={formData.config.qualityFilter.liquidity.minAvgVolume || 2000000}
                          onChange={(e) => handleQualityFilterChange('liquidity', 'minAvgVolume', e.target.value)}
                        />
                      </div>
                      <div className="setting-group">
                        <label>Min Current Vol % of Avg</label>
                        <input
                          type="number"
                          min="0"
                          max="200"
                          step="5"
                          value={formData.config.qualityFilter.liquidity.minCurrentVolumePct || 40}
                          onChange={(e) => handleQualityFilterChange('liquidity', 'minCurrentVolumePct', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="setting-row">
                      <div className="setting-group">
                        <label>Min Dollar Volume ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="1000000"
                          value={formData.config.qualityFilter.liquidity.minDollarVolume || 20000000}
                          onChange={(e) => handleQualityFilterChange('liquidity', 'minDollarVolume', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Quality Filter */}
              <div className="indicator-card" style={{ marginTop: '0.5rem' }}>
                <div className="indicator-header">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.config.qualityFilter.company?.enabled || false}
                      onChange={() => handleQualityFilterToggle('company.enabled')}
                    />
                    <span>Company Quality Filter</span>
                  </label>
                </div>
                {formData.config.qualityFilter.company?.enabled && (
                  <div className="indicator-settings">
                    <p className="info-text">Reject unprofitable or very small-cap stocks.</p>
                    <div className="setting-row">
                      <div className="setting-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.config.qualityFilter.company.requirePositiveEPS || false}
                            onChange={() => handleQualityFilterToggle('company.requirePositiveEPS')}
                          />
                          <span>Require Positive EPS</span>
                        </label>
                      </div>
                    </div>
                    <div className="setting-row">
                      <div className="setting-group">
                        <label>Min Market Cap ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="500000000"
                          value={formData.config.qualityFilter.company.minMarketCap || 3000000000}
                          onChange={(e) => handleQualityFilterChange('company', 'minMarketCap', e.target.value)}
                        />
                        <p className="info-text">Current: ${((formData.config.qualityFilter.company.minMarketCap || 3000000000) / 1e9).toFixed(1)}B</p>
                      </div>
                      <div className="setting-group">
                        <label>Unprofitable Override Cap ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="1000000000"
                          value={formData.config.qualityFilter.company.unprofitableOverrideCap || 0}
                          onChange={(e) => handleQualityFilterChange('company', 'unprofitableOverrideCap', e.target.value)}
                        />
                        <p className="info-text">Allow unprofitable if mkt cap above this (0 = no override)</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Score Threshold & Output */}
              <div className="indicator-card" style={{ marginTop: '0.5rem' }}>
                <div className="indicator-header">
                  <span style={{ fontWeight: 600 }}>Score Threshold & Output</span>
                </div>
                <div className="indicator-settings">
                  <div className="setting-row">
                    <div className="setting-group">
                      <label>Minimum Quality Score (0-100)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="5"
                        value={formData.config.qualityFilter.minScore || 65}
                        onChange={(e) => handleQualityFilterChange('root', 'minScore', e.target.value)}
                      />
                      <p className="info-text">A ≥ 80 | B ≥ 65 | C ≥ 50 | D &lt; 50</p>
                    </div>
                  </div>
                  <div className="setting-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.config.qualityFilter.showReasonBreakdown ?? true}
                        onChange={() => handleQualityFilterToggle('showReasonBreakdown')}
                      />
                      <span>Show reason breakdown on signals</span>
                    </label>
                  </div>
                  <div className="setting-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.config.qualityFilter.showWatchlistCandidates ?? true}
                        onChange={() => handleQualityFilterToggle('showWatchlistCandidates')}
                      />
                      <span>Show rejected candidates as watchlist signals</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Risk Management */}
      <div className="form-section">
        <h3>Risk Management</h3>
        <div className="risk-grid">
          <div className="form-group">
            <label htmlFor="riskPerTrade">Risk Per Trade ($)</label>
            <input
              type="number"
              id="riskPerTrade"
              min="0"
              step="1"
              value={formData.config.riskManagement.riskPerTrade || 0}
              onChange={(e) => handleRiskChange('riskPerTrade', e.target.value)}
            />
            <p className="info-text">Fixed dollar risk per trade (0 = disabled)</p>
          </div>
          <div className="form-group">
            <label htmlFor="rewardRiskRatio">Reward:Risk Ratio</label>
            <input
              type="number"
              id="rewardRiskRatio"
              min="0"
              step="0.01"
              max="10"
              value={formData.config.riskManagement.rewardRiskRatio || 0}
              onChange={(e) => handleRiskChange('rewardRiskRatio', e.target.value)}
            />
            <p className="info-text">
              {formData.config.riskManagement.rewardRiskRatio > 0 && formData.config.riskManagement.stopLoss > 0
                ? `Take profit auto-set to ${(formData.config.riskManagement.stopLoss * formData.config.riskManagement.rewardRiskRatio).toFixed(1)}%`
                : '0 = set take profit manually'}
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="stopLoss">Stop Loss (%)</label>
            <input
              type="number"
              id="stopLoss"
              min="0.1"
              step="0.1"
              max="10"
              value={formData.config.riskManagement.stopLoss}
              onChange={(e) => handleRiskChange('stopLoss', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="takeProfit">Take Profit (%)</label>
            <input
              type="number"
              id="takeProfit"
              min="0.1"
              step="0.1"
              max="20"
              value={formData.config.riskManagement.takeProfit}
              onChange={(e) => handleRiskChange('takeProfit', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="maxPosition">Max Position Size ($)</label>
            <input
              type="number"
              id="maxPosition"
              min="100"
              step="1"
              value={formData.config.riskManagement.maxPosition}
              onChange={(e) => handleRiskChange('maxPosition', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading || !selectedTemplate}>
          {loading ? 'Saving...' : initialData ? 'Update Strategy' : 'Create Strategy'}
        </button>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
      </>
      )}
    </form>
  );
}

export default StrategyForm;
